import { useState } from 'react';
import axios from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from 'lucide-react';
import {
  CREDENTIAL_TYPE_LABELS,
  createSealedCredential,
  type Credential,
  type CredentialCreateFields,
  type SealedCreateResponse,
} from '../hooks/useCredentials';
import type { SealableCredential } from '../lib/credentialSeal';
import { warnUndelivered } from './credentialDelivery';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

type CredType = Credential['type'];

/**
 * The types a user can create. Each carries a secret the browser seals for the
 * gateways, so the SaaS never sees it (docs/ClientSealedCredentials.md). X.509
 * is not offered: nothing logs in with a certificate (`credentialTypes.ts`).
 */
type CreatableType = SealableCredential['type'];
const ALL_TYPES: CreatableType[] = ['usernamePassword', 'sshKey', 'apiKey', 'preSharedKey', 'snmpV2Community', 'snmpV3'];
const isCreatable = (t: CredType): t is CreatableType => (ALL_TYPES as CredType[]).includes(t);
const NO_PROTOCOL = '__none__';

type CreateBody = CredentialCreateFields & SealableCredential;

interface ApiError {
  error?: string;
  description?: string;
}

interface CredentialCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Region the credential is created in. When `regions` is omitted this is the
   * fixed region — the new credential lands in the same region the calling
   * select filters by (and where the device it's for lives); every inline
   * caller has this in scope. When `regions` is provided this instead seeds the
   * region picker's initial selection (may be empty). */
  regionId: string;
  /** When provided, the dialog shows a region picker (seeded from `regionId`)
   * rather than pinning to a single region — used by the Credentials page,
   * where the user chooses which region the credential belongs to. */
  regions?: { id: string; name: string }[];
  /** Restrict the type picker to the types relevant to the calling select (SNMP
   * select → community/v3; SSH/HTTP → username-password/ssh-key). When exactly
   * one type is allowed the picker is hidden. Defaults to all types. */
  allowedTypes?: CredType[];
  /** Called with the created credential's id so the caller can select it. */
  onCreated: (credentialId: string) => void;
  /**
   * Steps to show above a type's fields while it is the chosen type: where the
   * caller's use makes one, say (a UniFi API key, with links into UniFi).
   */
  guidance?: Partial<Record<CredType, React.ReactNode>>;
}

/**
 * Create a credential inline, without leaving the flow. `CredentialSelect` opens
 * it from "+ New credential…", and the Credentials page from its own button: it
 * posts the same `{ name, regionId, description, type, …fields }` shape the
 * Credentials page uses and hands back the new id.
 */
export default function CredentialCreateDialog({
  open,
  onOpenChange,
  regionId,
  regions,
  allowedTypes,
  onCreated,
  guidance,
}: CredentialCreateDialogProps) {
  const offered = allowedTypes?.filter(isCreatable) ?? [];
  const types = offered.length > 0 ? offered : ALL_TYPES;
  const showRegionPicker = !!regions && regions.length > 0;
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [type, setType] = useState<CreatableType>(types[0]);
  const [region, setRegion] = useState(regionId);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [sshKey, setSshKey] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [preSharedKey, setPreSharedKey] = useState('');
  const [community, setCommunity] = useState('');
  const [v3User, setV3User] = useState('');
  const [v3AuthProto, setV3AuthProto] = useState(NO_PROTOCOL);
  const [v3AuthKey, setV3AuthKey] = useState('');
  const [v3PrivProto, setV3PrivProto] = useState(NO_PROTOCOL);
  const [v3PrivKey, setV3PrivKey] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Stays true from submit through the post-create refetch until the dialog
  // closes — covers the window where the POST has resolved but the credential
  // list is still refetching, so the button can't be double-clicked.
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setName('');
    setType(types[0]);
    setRegion(regionId);
    setUsername('');
    setPassword('');
    setSshKey('');
    setApiKey('');
    setPreSharedKey('');
    setCommunity('');
    setV3User('');
    setV3AuthProto(NO_PROTOCOL);
    setV3AuthKey('');
    setV3PrivProto(NO_PROTOCOL);
    setV3PrivKey('');
    setDescription('');
    setError(null);
    setBusy(false);
  };

  const close = () => {
    reset();
    onOpenChange(false);
  };

  // The dialog stays mounted (only `open` toggles), so `useState(types[0])` runs
  // just once with whatever `allowedTypes` was at first mount. Re-initialize to
  // the first allowed type — and a fresh form — each time it opens, so a select
  // that opens it scoped to SNMP doesn't show the previous type's fields.
  // Only on open transitions — resetting whenever `types` changed would wipe the form
  // on every keystroke. Done during render so the previous form never paints.
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) reset();
  }

  const mutation = useMutation<SealedCreateResponse, Error, CreateBody>({
    mutationFn: createSealedCredential,
    onSuccess: async (created) => {
      // Await the refetch so the new credential is in the list BEFORE we select
      // it — a Select (Radix or native) won't display a value whose option
      // doesn't exist yet, so selecting before the list refreshes silently
      // no-ops. In React Query v5 invalidateQueries resolves once active queries
      // have refetched.
      await queryClient.invalidateQueries({ queryKey: ['credentials'] });
      onCreated(created.id);
      close();
      warnUndelivered('Credential saved', created.undelivered);
    },
    onError: (err) => {
      if (axios.isAxiosError<ApiError>(err)) {
        setError(err.response?.data.description ?? err.response?.data.error ?? 'Failed to create credential.');
      } else {
        // Sealing refused before anything was sent (a bad gateway key, say).
        setError(err.message);
      }
      setBusy(false);
    },
  });

  const protoOrNull = (v: string) => (v === NO_PROTOCOL ? null : v);

  const submit = () => {
    setError(null);
    if (!name) {
      setError('Credential name is required.');
      return;
    }
    if (showRegionPicker && !region) {
      setError('Region is required.');
      return;
    }
    const common = { name, regionId: region, description: description || null };
    let body: CreateBody;
    switch (type) {
      case 'usernamePassword':
        if (!username || !password) {
          setError('Username and password are required.');
          return;
        }
        body = { ...common, type, username, password };
        break;
      case 'sshKey':
        if (!sshKey) {
          setError('SSH key content is required.');
          return;
        }
        body = { ...common, type, key: sshKey };
        break;
      case 'apiKey':
        if (!apiKey) {
          setError('API key is required.');
          return;
        }
        body = { ...common, type, key: apiKey };
        break;
      case 'preSharedKey':
        if (!preSharedKey) {
          setError('The pre-shared key is required.');
          return;
        }
        body = { ...common, type, key: preSharedKey };
        break;
      case 'snmpV2Community':
        if (!community) {
          setError('Community string is required.');
          return;
        }
        body = { ...common, type, community };
        break;
      case 'snmpV3':
        if (!v3User) {
          setError('Username is required for SNMPv3.');
          return;
        }
        body = {
          ...common,
          type,
          username: v3User,
          authProtocol: protoOrNull(v3AuthProto),
          authKey: v3AuthKey || null,
          privProtocol: protoOrNull(v3PrivProto),
          privKey: v3PrivKey || null,
        };
        break;
    }
    setBusy(true);
    mutation.mutate(body);
  };

  const hasAuth = v3AuthProto !== NO_PROTOCOL;
  const hasPriv = v3PrivProto !== NO_PROTOCOL;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New credential</DialogTitle>
        </DialogHeader>

        {error ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="ncd-name">Name</Label>
            <Input
              id="ncd-name"
              value={name}
              onChange={(e) => { setName(e.target.value); }}
              placeholder="e.g., office-snmp"
            />
          </div>

          {showRegionPicker ? (
            <div className="space-y-1.5">
              <Label htmlFor="ncd-region">Region</Label>
              <Select value={region} onValueChange={(v) => { if (v) setRegion(v); }}>
                <SelectTrigger id="ncd-region">
                  <SelectValue placeholder="Select a region" />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {types.length > 1 ? (
            <div className="space-y-1.5">
              <Label htmlFor="ncd-type">Type</Label>
              <Select value={type} onValueChange={(v) => { setType(v as CreatableType); }}>
                <SelectTrigger id="ncd-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {types.map((t) => (
                    <SelectItem key={t} value={t}>
                      {CREDENTIAL_TYPE_LABELS[t]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null}

          {guidance?.[type] ? (
            <div className="rounded-lg border bg-muted/40 p-3 text-sm">{guidance[type]}</div>
          ) : null}

          {type === 'usernamePassword' ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="ncd-user">Username</Label>
                <Input id="ncd-user" value={username} onChange={(e) => { setUsername(e.target.value); }} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ncd-pass">Password</Label>
                <Input
                  id="ncd-pass"
                  type="password"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); }}
                />
              </div>
            </>
          ) : null}

          {type === 'sshKey' ? (
            <div className="space-y-1.5">
              <Label htmlFor="ncd-sshkey">SSH private key</Label>
              <Textarea
                id="ncd-sshkey"
                value={sshKey}
                onChange={(e) => { setSshKey(e.target.value); }}
                rows={4}
                className="font-mono"
                placeholder="Paste your SSH private key here"
              />
            </div>
          ) : null}

          {type === 'apiKey' ? (
            <div className="space-y-1.5">
              <Label htmlFor="ncd-apikey">API key</Label>
              <Input
                id="ncd-apikey"
                type="password"
                autoComplete="off"
                value={apiKey}
                onChange={(e) => { setApiKey(e.target.value); }}
                className="font-mono"
              />
            </div>
          ) : null}

          {type === 'preSharedKey' ? (
            <div className="space-y-1.5">
              <Label htmlFor="ncd-psk">Pre-shared key</Label>
              <Input
                id="ncd-psk"
                type="password"
                autoComplete="off"
                value={preSharedKey}
                onChange={(e) => { setPreSharedKey(e.target.value); }}
              />
              <p className="text-xs text-muted-foreground">
                One secret both ends share, such as a Wi-Fi passphrase.
              </p>
            </div>
          ) : null}

          {type === 'snmpV2Community' ? (
            <div className="space-y-1.5">
              <Label htmlFor="ncd-community">Community string</Label>
              <Input
                id="ncd-community"
                value={community}
                onChange={(e) => { setCommunity(e.target.value); }}
                className="font-mono"
              />
            </div>
          ) : null}

          {type === 'snmpV3' ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="ncd-v3user">Username</Label>
                <Input id="ncd-v3user" value={v3User} onChange={(e) => { setV3User(e.target.value); }} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ncd-v3auth">Authentication protocol (optional)</Label>
                <Select value={v3AuthProto} onValueChange={setV3AuthProto}>
                  <SelectTrigger id="ncd-v3auth">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_PROTOCOL}>None (NoAuthNoPriv)</SelectItem>
                    <SelectItem value="MD5">MD5</SelectItem>
                    <SelectItem value="SHA">SHA</SelectItem>
                    <SelectItem value="SHA256">SHA-256</SelectItem>
                    <SelectItem value="SHA384">SHA-384</SelectItem>
                    <SelectItem value="SHA512">SHA-512</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {hasAuth ? (
                <div className="space-y-1.5">
                  <Label htmlFor="ncd-v3authkey">Authentication key</Label>
                  <Input
                    id="ncd-v3authkey"
                    type="password"
                    value={v3AuthKey}
                    onChange={(e) => { setV3AuthKey(e.target.value); }}
                  />
                </div>
              ) : null}
              <div className="space-y-1.5">
                <Label htmlFor="ncd-v3priv">Privacy protocol (optional)</Label>
                <Select value={v3PrivProto} onValueChange={setV3PrivProto}>
                  <SelectTrigger id="ncd-v3priv">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_PROTOCOL}>None (AuthNoPriv)</SelectItem>
                    <SelectItem value="DES">DES</SelectItem>
                    <SelectItem value="AES">AES-128</SelectItem>
                    <SelectItem value="AES192">AES-192</SelectItem>
                    <SelectItem value="AES256">AES-256</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {hasPriv ? (
                <div className="space-y-1.5">
                  <Label htmlFor="ncd-v3privkey">Privacy key</Label>
                  <Input
                    id="ncd-v3privkey"
                    type="password"
                    value={v3PrivKey}
                    onChange={(e) => { setV3PrivKey(e.target.value); }}
                  />
                </div>
              ) : null}
            </>
          ) : null}

          <div className="space-y-1.5">
            <Label htmlFor="ncd-desc">Description (optional)</Label>
            <Textarea id="ncd-desc" value={description} onChange={(e) => { setDescription(e.target.value); }} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" type="button" onClick={close} disabled={busy}>
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={busy}>
            {busy ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
